import { Add } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, Grid, IconButton, InputAdornment, Paper, TextField, Tooltip, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { getOrderReviewers, postFacilityUserReviewerForm } from '../../../../../api/facility';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import { ChevronBox } from '../../../../../reusable-components/chevron-box';
import { ConfirmDialog } from '../../../../../reusable-components/confirm-dialog';
import FormDropdown from '../../../../../reusable-components/form/FormDropdown';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import BudgetMenu from './BudgetMenu';
import CategoryPicker from './CategoryPicker';
import IconBadge from './IconBadge';
import MultisetReviewers from './MultisetReviewers';
import RequiredOptions from './RequiredOptions';

ReviewWorkflowTab.propTypes = {
  selectedRow: PropTypes.object,
};
export default function ReviewWorkflowTab({ selectedRow }) {
  const { enqueueSnackbar } = useSnackbar();
  const [userData, setUserData] = useState([]);

  const [editSectionOpen, setEditSectionOpen] = useState([]);
  const [loadingSection, setLoadingSection] = useState(-1);
  const [value, setValue] = useState({});
  const [originalValue, setOriginalValue] = useState({});
  const [categories, setCategories] = useState([]);

  const handleGetData = async () => {
    try {
      const response = await getOrderReviewers(selectedRow.id);
      if (response.data) {
        const usersMap = response.data.facilityUsers?.map((user) => ({
          name: `${user.name} - ${user.position}`,
          email: user.email,
          reviewers: [
            ...response.data.reviewers
              ?.filter((rev) => rev.facilityUserEmail === user.email)
              ?.map((rev) => ({
                email: rev.reviewerEmail,
                name: rev.reviewerName,
                id: rev.orderReviewerId,
                required: rev.required,
                minimum: rev.minimum,
                budget: rev.budget,
                categories: rev.categories.length ? rev.categories : response.data.categories,
              })),
          ],
        }));
        const facilityReviewsMap = response.data.reviewers
          ?.filter((rev) => rev.facilityUserEmail === null)
          .map((rev) => ({
            email: rev.reviewerEmail,
            name: rev.reviewerName,
            id: rev.orderReviewerId,
            required: rev.required,
            minimum: rev.minimum,
            budget: rev.budget,
            categories: rev.categories.length ? rev.categories : response.data.categories,
          }));
        setValue({ reviewers: facilityReviewsMap, users: usersMap });
        setOriginalValue(JSON.parse(JSON.stringify({ reviewers: facilityReviewsMap, users: usersMap })));
        setCategories(response.data.categories);
        setUserData(response.data.users?.map((u) => ({ email: u.email, name: `${u.firstName} ${u.lastName}` })));
      }
    } catch (error) {
      enqueueSnackbar("An error occurred getting reviewers.", { variant: "error" });
    }
  };

  useEffect(() => {
    handleGetData();
  }, []);

  const handleAddSection = (index) => {
    setValue((prevValue) => {
      const newUsers = [...prevValue.users];
      const newUserReviewers = [...newUsers[index]?.reviewers];
      newUserReviewers.push({ categories });
      newUsers[index] = { ...newUsers[index], reviewers: newUserReviewers };
      return { ...prevValue, users: newUsers };
    });
  };

  const handleEditSection = (index) => {
    if (editSectionOpen.includes(index)) {
      setEditSectionOpen(editSectionOpen.filter((number) => number !== index));

      setValue(prevValue => {
        const currentUsers = [...prevValue?.users];
        const originalUsers = [...originalValue?.users];
        currentUsers[index] = { ...originalUsers[index] };
        return { ...prevValue, users: currentUsers };
      })
    }
    else setEditSectionOpen([...editSectionOpen, index]);
  };

  const handleEditUserLevelSave = async (index) => {
    setLoadingSection(index);
    try {
      const response = await postFacilityUserReviewerForm(value?.users[index], selectedRow.id);
      if (response.data.error) {
        enqueueSnackbar(`Error: ${response.data.error}`, {
          variant: 'error',
        });
      }
      else if (response.status === 200) {
        enqueueSnackbar(`Successfully updated facility reviewers`, {
          variant: 'success',
        });
      } else
        enqueueSnackbar(`Error: Could not update reviewers`, {
          variant: 'error',
        });
      handleGetData();
    } catch (error) {
      enqueueSnackbar(`Error: Could not update reviewers`, { variant: 'error' });
    }
    setLoadingSection(-1);
  };

  const setUserLevelField = (newValue, userIndex, reviewerIndex, updateField) => {
    setValue(prevValue => {
      const newUsers = [...prevValue?.users];
      const newUserReviewers = [...newUsers[userIndex]?.reviewers];
      newUserReviewers[reviewerIndex] = {
        ...newUserReviewers[reviewerIndex]
        , [updateField]: newValue
      };
      newUsers[userIndex] = { ...newUsers[userIndex], reviewers: newUserReviewers };
      return { ...prevValue, users: newUsers };
    })
  }

  const disabledSectionIndexes = useMemo(() =>
    (value.users || []).reduce((acc, user, idx) => {
      if (user.reviewers.some(
        reviewer => !reviewer.email || !reviewer.categories || reviewer.categories.length === 0
      )) acc.push(idx);
      return acc;
    }, [])
    , [value]);

  return (
    <Paper sx={{ mt: 2, px: 8, pb: 4, pt: 4 }}>
      <Typography variant="h3" color="info.main">
        {selectedRow.facilityName} | Review Workflow
      </Typography>
      <Grid container direction="row" sx={{ mt: 2 }} gap={2}>
        <Grid item>
          <MultisetReviewers selectedRow={selectedRow}
            value={value} setValue={setValue} userData={userData}
            categories={categories} originalValue={originalValue}
            handleGetData={handleGetData} />
        </Grid>
        <Grid item>
          {(value.reviewers?.length > 1 || value.users?.some(user => user.reviewers?.length > 1)) &&
            <RequiredOptions facilityId={selectedRow.id} />
          }
        </Grid>
      </Grid>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2, mt: 2 }}
      >
        <Grid item>
          <Typography variant="subtitle1" color="info.main">
            Order Requesters & Reviewers
          </Typography>
        </Grid>
      </Grid>
      {value?.users?.length > 0 &&
        value?.users?.map((user, index) => (
          <Box key={user.id}>
            <Box sx={{ mx: 4, my: 2 }}>
              <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={1} >
                <Typography sx={{ mr: 1 }}> <strong>{index + 1})</strong> {value?.users[index]?.name}</Typography>
                {!editSectionOpen?.includes(index) &&
                  <Grid item>
                    <Tooltip title="Edit or add facility employee reviewer">
                      <IconButton disabled={loadingSection > -1} onClick={() => handleEditSection(index)} sx={{ color: '#212B36', '&:hover': { color: editSectionOpen?.includes(index) ? 'error.main' : 'info.main' } }}>
                        <Add />
                      </IconButton>
                    </Tooltip>
                  </Grid>}
                {(editSectionOpen?.includes(index)) &&
                  <Grid item>
                    <Tooltip title="Add facility employee reviewer">
                      <Button endIcon={<Add />} color="secondary" size="small" variant="outlined"
                        disabled={loadingSection > -1}
                        onClick={() => handleAddSection(index)} >
                        Add
                      </Button>
                    </Tooltip>
                    <Tooltip title={_.isEqual(value.users[index], originalValue.users[index]) ? "No changes to save"
                      : disabledSectionIndexes?.includes(index) ? "you are missing a required field" : ""}><span>
                        <AwaitButton loading={loadingSection === index}
                          sx={{ ml: 2 }} variant="contained" color="secondary" size="small"
                          disabled={loadingSection > -1 || disabledSectionIndexes?.includes(index)
                            || _.isEqual(value.users[index], originalValue.users[index])}
                          onClick={() => { handleEditUserLevelSave(index); handleEditSection(index); }} >
                          Save
                        </AwaitButton>
                      </span></Tooltip>
                    <Button variant="text" color="secondary" size="small" sx={{ ml: 1, textDecoration: "underline" }}
                      disabled={loadingSection > -1} onClick={() => handleEditSection(index)}>
                      Cancel
                    </Button>
                  </Grid>
                }

                {!editSectionOpen?.includes(index) && user?.reviewers?.length > 0 &&
                  user?.reviewers?.map((reviewer, reviewerIndex) => {
                    const colors = ['#90269d', 'secondary.main', '#cc6ad3'];
                    return <> {reviewer?.name?.length > 0 &&
                      <Box sx={{ position: "relative", display: "inline-block" }} >
                        <ChevronBox
                          color={colors[reviewerIndex % colors.length]}
                          reviewerIndex={reviewerIndex}
                          length={user?.reviewers.length}
                          children={
                            <Typography>
                              {reviewer?.name}
                            </Typography>} />
                        <Box sx={{ position: "absolute", top: -12, right: 4, display: "flex", gap: "4px" }}>
                          {!!reviewer.minimum && <IconBadge title={`Review orders over $${reviewer.minimum}`} color="error" icon="lsicon:amount-dollar-outline" />}
                          {!!reviewer.budget && <IconBadge title={`Review orders over the ${reviewer.budget} budget`} color="info" icon="tabler:calendar-dollar" />}
                          {!!reviewer.categories.length &&
                            <IconBadge title={`Review categories: ${reviewer.categories.map(c => c.name).join(", ")}`} color="primary" icon="tabler:category-plus" />}
                          {!!reviewer.required && <IconBadge title={`Review required`} color="warning" icon="mynaui:asterisk-circle" />}
                        </Box>
                      </Box>
                    }
                    </>
                  })
                }
              </Grid>
            </Box>
            <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
              {user?.reviewers?.length > 0 &&
                user?.reviewers?.map((_, reviewerIndex) =>
                  <>
                    <Grid item xs={4}>
                      {(editSectionOpen?.includes(index)) &&
                        <Grid container sx={{ ml: 4 }} alignItems="flex-start" spacing={2}>
                          <Grid item sx={{ mt: 1.5 }}>
                            <Typography variant="subtitle1" >Reviewer {reviewerIndex + 1}</Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Grid container direction="row">
                              <Grid item xs={10}>
                                <FormDropdown
                                  disableClearable
                                  disabled={!editSectionOpen?.includes(index)}
                                  fullWidth
                                  value={value?.users[index]?.reviewers[reviewerIndex]}
                                  onChange={(_, newValue) => {
                                    setUserLevelField(newValue.email, index, reviewerIndex, "email")
                                    setUserLevelField(newValue.name, index, reviewerIndex, "name")
                                    setUserLevelField(newValue.orderReviewerId, index, reviewerIndex, "id")
                                  }}
                                  options={userData}
                                  isOptionEqualToValue={(option, value) => option?.email === value?.email}
                                  getOptionLabel={(option) => option?.name ? `${option?.name} - ${option?.email}` : ''}
                                  renderInput={(params) => <TextField {...params} label="Search or select" autoComplete='off'/>}
                                />
                              </Grid>

                              <ConfirmDialog
                                icon="delete-outline"
                                title="Delete"
                                color="error"
                                content={`Deleting a reviewer from the facility completely will delete their permission to edit orders`}
                                actionButton="Delete"
                                action={() => {
                                  setValue(prevValue => {
                                    const usersArr = [...prevValue.users];
                                    const reviewersArr = [...usersArr[index]?.reviewers]
                                    reviewersArr.splice(reviewerIndex, 1);
                                    usersArr[index] = { ...usersArr[index], reviewers: reviewersArr };
                                    return { ...prevValue, users: usersArr }
                                  })
                                }}
                              />
                              <Grid item container direction="column" alignItems="flex-start" sx={{ ml: 0.5, mt: 1 }} >
                                <Grid item container direction="row" alignItems="center" >
                                  <Checkbox color="secondary" size="small" sx={{ py: 0 }}
                                    checked={!!value?.users[index]?.reviewers[reviewerIndex]?.categories.length}
                                    onChange={(event) => {
                                      const newValue = event.target.checked ? categories : [];
                                      setUserLevelField(newValue, index, reviewerIndex, "categories");
                                    }}
                                  />
                                  <CategoryPicker
                                    value={value?.users[index]?.reviewers[reviewerIndex]?.categories}
                                    onChange={(newValue) => setUserLevelField(newValue, index, reviewerIndex, "categories")}
                                    categories={categories} />
                                </Grid>
                                <Grid item container direction="row" alignItems="center" >
                                  <Typography variant="caption">
                                    <Checkbox color="secondary" size="small" sx={{ py: 0 }}
                                      checked={!!value?.users[index]?.reviewers[reviewerIndex]?.minimum}
                                      onChange={(event) => {
                                        const newValue = event.target.checked ? "0" : "";
                                        setUserLevelField(newValue, index, reviewerIndex, "minimum");
                                      }}
                                    />
                                    Review if over
                                  </Typography>
                                  <TextField
                                    size="small"
                                    type="number"
                                    variant="standard"
                                    autoComplete='off'
                                    sx={{ maxWidth: 70, ml: 3 }}
                                    InputLabelProps={{ style: { fontSize: 12, lineHeight: 3, p: 0 } }} // font size of input label
                                    InputProps={{
                                      sx: { fontSize: 12, p: 0 },
                                      startAdornment: <InputAdornment size="small" position="start"><Typography variant="caption">$</Typography></InputAdornment>,
                                    }}
                                    value={value?.users[index]?.reviewers[reviewerIndex].minimum}
                                    onChange={(event) => setUserLevelField(event.target.value, index, reviewerIndex, "minimum")}
                                  />
                                </Grid>
                                <Grid item container direction="row" alignItems="center">
                                  <Checkbox size="small" sx={{ py: 0 }} color="secondary"
                                    onChange={(event) => {
                                      const newValue = event.target.checked ? "monthly" : undefined;
                                      setUserLevelField(newValue, index, reviewerIndex, "budget");
                                    }}
                                    checked={!!value?.users[index]?.reviewers[reviewerIndex].budget} />

                                  <BudgetMenu menuElements={["monthly", "weekly"]}
                                    value={value?.users[index]?.reviewers[reviewerIndex].budget}
                                    onChange={(newValue) => setUserLevelField(newValue, index, reviewerIndex, "budget")} />
                                </Grid>
                                <Grid item>
                                  <Checkbox size="small" sx={{ py: 0 }} color="secondary"
                                    checked={value?.users[index]?.reviewers[reviewerIndex].required}
                                    onChange={(_, newValue) => setUserLevelField(newValue, index, reviewerIndex, "required")}
                                  />
                                  <Typography variant="caption"> Review Required </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      }
                    </Grid>
                  </>)
              }
            </Grid>
            <Divider />
          </Box>
        ))}
    </Paper>
  );
}
